import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, authFetch } from "../App";
import { toast } from "sonner";
import {
  FileText, Plus, Upload, Trash2, Download, ExternalLink, File,
  FileSpreadsheet, Image, FolderOpen, ArrowLeft, Search, RefreshCw,
  Loader2, X, ChevronLeft, AlertTriangle, Zap,
} from "lucide-react";
import ProjectTopNav from "../components/ProjectTopNav";

const API = "/api";

const CATEGORIES = [
  { value: "vertrag", label: "Vertrag", folder: "01_Vertrag" },
  { value: "planung", label: "Planung", folder: "02_Planung" },
  { value: "ausfuehrung", label: "Ausfuehrung", folder: "03_Ausfuehrung" },
  { value: "dokumentation", label: "Dokumentation", folder: "04_Dokumentation" },
  { value: "abrechnung", label: "Abrechnung", folder: "05_Abrechnung" },
  { value: "korrespondenz", label: "Korrespondenz", folder: "06_Korrespondenz" },
  { value: "nachtraege", label: "Nachtraege", folder: "07_Nachtraege" },
];

const CAT_COLORS = {
  vertrag: "#3b82f6", planung: "#8b5cf6", ausfuehrung: "#f59e0b",
  dokumentation: "#10b981", abrechnung: "#ef4444", korrespondenz: "#06b6d4",
  nachtraege: "#f97316",
};

function getFileIcon(mime) {
  if (!mime) return FileText;
  if (mime.includes("pdf")) return FileText;
  if (mime.includes("image")) return Image;
  if (mime.includes("sheet") || mime.includes("excel")) return FileSpreadsheet;
  return File;
}

function fmtSize(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}

export default function ProjectDocuments() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadCat, setUploadCat] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [filterCat, setFilterCat] = useState("all");
  const [search, setSearch] = useState("");
  const [driveStatus, setDriveStatus] = useState(null);

  // Load project
  useEffect(() => {
    authFetch(`${API}/projects/${projectId}`).then(r => r.ok ? r.json() : null)
      .then(p => { if (p) setProject(p); }).catch(() => {});
  }, [projectId]);

  // Check drive status
  useEffect(() => {
    authFetch(`${API}/drive/status`).then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setDriveStatus(d); }).catch(() => {});
  }, []);

  // Load files
  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/drive/list?project_id=${projectId}`);
      if (res.ok) {
        const d = await res.json();
        setFiles(d.files || []);
      }
    } catch {}
    setLoading(false);
  }, [projectId]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  const [aiResult, setAiResult] = useState(null);

  const handleSmartUpload = async () => {
    if (!uploadFile) { toast.error("Bitte Datei auswaehlen"); return; }
    setUploading(true);
    setAiResult(null);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("project_id", projectId);
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/drive/smart-upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        const ai = data.ai || {};
        setAiResult(ai);
        const catLabel = CATEGORIES.find(c => c.value === ai.category)?.label || ai.category;
        toast.success(`Einsortiert in: ${catLabel}`);
        setShowUpload(false);
        setUploadFile(null);
        setUploadCat("");
        loadFiles();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Upload fehlgeschlagen");
      }
    } catch { toast.error("Netzwerkfehler"); }
    setUploading(false);
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadCat) {
      toast.error("Datei und Kategorie sind Pflichtfelder");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("project_id", projectId);
      formData.append("category", uploadCat);
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/drive/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        toast.success("Datei hochgeladen!");
        setShowUpload(false);
        setUploadFile(null);
        setUploadCat("");
        loadFiles();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Upload fehlgeschlagen");
      }
    } catch {
      toast.error("Netzwerkfehler");
    }
    setUploading(false);
  };

  const openFile = async (doc) => {
    if (doc.google_drive_url) {
      window.open(doc.google_drive_url, "_blank");
    } else {
      try {
        const res = await authFetch(`${API}/drive/file/${doc.id}`);
        if (res.ok) {
          const d = await res.json();
          if (d.webViewLink) window.open(d.webViewLink, "_blank");
        }
      } catch {}
    }
  };

  const isConnected = driveStatus?.ok && driveStatus?.shared_drive_found;
  const filtered = files.filter(f => {
    if (filterCat !== "all" && f.category !== filterCat) return false;
    if (search && !f.filename?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Group by category
  const catCounts = {};
  files.forEach(f => { catCounts[f.category] = (catCounts[f.category] || 0) + 1; });

  return (
    <div className="space-y-6" data-testid="project-documents-page">
      <ProjectTopNav projectId={projectId} />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/app/projekte/${projectId}/dashboard`)}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="docs-back">
            <ChevronLeft className="w-5 h-5" style={{ color: "var(--tga-text-secondary)" }} />
          </button>
          <div>
            <h1 className="text-lg font-bold" style={{ color: "var(--tga-text)" }}>Dokumente</h1>
            {project && <p className="text-xs" style={{ color: "var(--tga-text-secondary)" }}>{project.display_name || project.name}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadFiles} className="p-2 rounded-lg hover:bg-white/10" data-testid="docs-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} style={{ color: "var(--tga-text-secondary)" }} />
          </button>
          <button onClick={() => setShowUpload(!showUpload)} disabled={!isConnected}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white transition-all"
            data-testid="docs-upload-btn">
            <Upload className="w-4 h-4" /> Hochladen
          </button>
        </div>
      </div>

      {/* Drive Status Warning */}
      {driveStatus && !isConnected && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-500/30 bg-amber-500/5" data-testid="drive-warning">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-300">Google Drive nicht verbunden</p>
            <p className="text-xs text-amber-400/70 mt-1">{driveStatus.message}</p>
          </div>
        </div>
      )}

      {/* Upload Form */}
      {showUpload && (
        <div className="rounded-xl border p-5 space-y-4" style={{ borderColor: "var(--tga-border)", backgroundColor: "var(--tga-bg-card)" }} data-testid="upload-form">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold" style={{ color: "var(--tga-text)" }}>Datei hochladen</h2>
            <button onClick={() => { setShowUpload(false); setUploadFile(null); setUploadCat(""); }} className="p-1 rounded hover:bg-white/10">
              <X className="w-4 h-4" style={{ color: "var(--tga-text-secondary)" }} />
            </button>
          </div>

          {/* Category */}
          <div>
            <label className="text-xs font-semibold block mb-2" style={{ color: "var(--tga-text-secondary)" }}>Kategorie (Pflicht)</label>
            <div className="grid grid-cols-4 gap-2">
              {CATEGORIES.map(cat => (
                <button key={cat.value} onClick={() => setUploadCat(cat.value)}
                  className={`h-10 rounded-lg border text-xs font-medium transition-all ${uploadCat === cat.value ? "text-white" : "hover:border-white/20"}`}
                  style={{
                    backgroundColor: uploadCat === cat.value ? CAT_COLORS[cat.value] + "30" : undefined,
                    borderColor: uploadCat === cat.value ? CAT_COLORS[cat.value] : "var(--tga-border)",
                    color: uploadCat === cat.value ? CAT_COLORS[cat.value] : "var(--tga-text-secondary)",
                  }}
                  data-testid={`upload-cat-${cat.value}`}>
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* File */}
          <div>
            <label className="text-xs font-semibold block mb-2" style={{ color: "var(--tga-text-secondary)" }}>Datei</label>
            {uploadFile ? (
              <div className="flex items-center gap-3 p-3 rounded-lg border" style={{ borderColor: "var(--tga-border)" }}>
                <FileText className="w-5 h-5" style={{ color: "var(--tga-text-secondary)" }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate" style={{ color: "var(--tga-text)" }}>{uploadFile.name}</p>
                  <p className="text-[10px]" style={{ color: "var(--tga-text-secondary)" }}>{fmtSize(uploadFile.size)}</p>
                </div>
                <button onClick={() => setUploadFile(null)} className="p-1 rounded hover:bg-white/10">
                  <X className="w-4 h-4" style={{ color: "var(--tga-text-secondary)" }} />
                </button>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 h-20 rounded-lg border-2 border-dashed cursor-pointer hover:border-blue-500/40 transition-colors"
                style={{ borderColor: "var(--tga-border)" }} data-testid="upload-file-label">
                <Plus className="w-6 h-6" style={{ color: "var(--tga-text-secondary)" }} />
                <span className="text-sm" style={{ color: "var(--tga-text-secondary)" }}>Datei auswaehlen oder ziehen</span>
                <input type="file" className="hidden" onChange={e => setUploadFile(e.target.files?.[0])} data-testid="upload-file-input" />
              </label>
            )}
          </div>

          <div className="flex gap-2">
            <button onClick={handleSmartUpload} disabled={uploading || !uploadFile}
              className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all"
              data-testid="upload-smart">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {uploading ? "KI sortiert..." : "KI-Upload"}
            </button>
            <button onClick={handleUpload} disabled={uploading || !uploadFile || !uploadCat}
              className="flex-1 h-11 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all"
              data-testid="upload-submit">
              <Upload className="w-4 h-4" /> Manuell
            </button>
          </div>
        </div>
      )}

      {/* AI Result */}
      {aiResult && (
        <div className="rounded-xl border p-4 bg-emerald-500/5 border-emerald-500/20" data-testid="ai-result">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-1">
              <p className="text-sm font-semibold text-emerald-300">KI-Einsortierung</p>
              <p className="text-xs" style={{ color: "var(--tga-text)" }}>
                <span className="opacity-50">Kategorie:</span> <span className="text-emerald-400">{CATEGORIES.find(c => c.value === aiResult.category)?.label || aiResult.category}</span>
              </p>
              <p className="text-xs" style={{ color: "var(--tga-text)" }}>
                <span className="opacity-50">Typ:</span> {aiResult.document_type}
              </p>
              <p className="text-xs" style={{ color: "var(--tga-text)" }}>
                <span className="opacity-50">Dateiname:</span> {aiResult.suggested_filename}
              </p>
              <p className="text-xs" style={{ color: "var(--tga-text)" }}>
                <span className="opacity-50">Sicherheit:</span> {Math.round((aiResult.confidence || 0) * 100)}%
              </p>
              {aiResult.reasoning && (
                <p className="text-[10px] italic" style={{ color: "var(--tga-text-secondary)" }}>{aiResult.reasoning}</p>
              )}
            </div>
            <button onClick={() => setAiResult(null)} className="p-1 rounded hover:bg-white/10">
              <X className="w-3.5 h-3.5" style={{ color: "var(--tga-text-secondary)" }} />
            </button>
          </div>
        </div>
      )}

      {/* Search + Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--tga-text-secondary)" }} />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Dokument suchen..."
            className="w-full h-9 pl-9 pr-3 rounded-lg border text-sm outline-none"
            style={{ borderColor: "var(--tga-border)", backgroundColor: "var(--tga-bg-card)", color: "var(--tga-text)" }}
            data-testid="docs-search"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => setFilterCat("all")}
            className={`px-3 py-1 rounded-full text-[10px] font-semibold transition-all ${filterCat === "all" ? "bg-white/15 text-white" : "bg-white/5 text-gray-500"}`}
            data-testid="filter-all">
            Alle ({files.length})
          </button>
          {Object.entries(catCounts).map(([cat, count]) => (
            <button key={cat} onClick={() => setFilterCat(cat)}
              className="px-3 py-1 rounded-full text-[10px] font-semibold transition-all"
              style={{
                backgroundColor: filterCat === cat ? (CAT_COLORS[cat] || "#6b7280") + "30" : "rgba(255,255,255,0.05)",
                color: filterCat === cat ? (CAT_COLORS[cat] || "#6b7280") : "var(--tga-text-secondary)",
              }}
              data-testid={`filter-${cat}`}>
              {CATEGORIES.find(c => c.value === cat)?.label || cat} ({count})
            </button>
          ))}
        </div>
      </div>

      {/* File List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--tga-text-secondary)" }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-20" style={{ color: "var(--tga-text-secondary)" }} />
          <p className="text-sm font-medium" style={{ color: "var(--tga-text-secondary)" }}>
            {files.length === 0 ? "Keine Dokumente vorhanden" : "Keine Ergebnisse"}
          </p>
          <p className="text-xs mt-1" style={{ color: "var(--tga-text-secondary)" }}>
            {isConnected ? "Laden Sie Dokumente ueber den Upload-Button hoch" : "Google Drive ist nicht verbunden"}
          </p>
        </div>
      ) : (
        <div className="border rounded-xl overflow-hidden" style={{ borderColor: "var(--tga-border)" }} data-testid="docs-table">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: "var(--tga-bg-card)" }}>
                <th className="text-left px-4 py-2.5 text-xs font-semibold" style={{ color: "var(--tga-text-secondary)" }}>Datei</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold hidden sm:table-cell" style={{ color: "var(--tga-text-secondary)" }}>Kategorie</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold hidden md:table-cell" style={{ color: "var(--tga-text-secondary)" }}>Groesse</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold hidden md:table-cell" style={{ color: "var(--tga-text-secondary)" }}>Datum</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold hidden lg:table-cell" style={{ color: "var(--tga-text-secondary)" }}>Hochgeladen von</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(doc => {
                const Icon = getFileIcon(doc.mime_type);
                const catColor = CAT_COLORS[doc.category] || "#6b7280";
                return (
                  <tr key={doc.id}
                    className="border-t hover:bg-white/[0.03] cursor-pointer transition-colors"
                    style={{ borderColor: "var(--tga-border)" }}
                    onClick={() => openFile(doc)}
                    data-testid={`doc-row-${doc.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: catColor + "18" }}>
                          <Icon className="w-4 h-4" style={{ color: catColor }} />
                        </div>
                        <span className="font-medium truncate max-w-[300px]" style={{ color: "var(--tga-text)" }}>{doc.filename}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: catColor + "20", color: catColor }}>
                        {CATEGORIES.find(c => c.value === doc.category)?.label || doc.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs hidden md:table-cell" style={{ color: "var(--tga-text-secondary)" }}>{fmtSize(doc.size)}</td>
                    <td className="px-4 py-3 text-xs hidden md:table-cell" style={{ color: "var(--tga-text-secondary)" }}>
                      {doc.created_at ? new Date(doc.created_at).toLocaleDateString("de-DE") : ""}
                    </td>
                    <td className="px-4 py-3 text-xs hidden lg:table-cell" style={{ color: "var(--tga-text-secondary)" }}>{doc.user_name}</td>
                    <td className="px-2 py-3">
                      <ExternalLink className="w-4 h-4 opacity-30 hover:opacity-70 transition-opacity" style={{ color: "var(--tga-text-secondary)" }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Path info */}
      {isConnected && project && (
        <p className="text-[10px] font-mono opacity-30 text-center" style={{ color: "var(--tga-text-secondary)" }}>
          Google Drive: TGAone/01_Projekte/{project.gdrive_folder_name || project.display_name || project.name}/...
        </p>
      )}
    </div>
  );
}
